import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IconBrandAndroid, IconBrandDjango, IconBrandPython, IconBrandReact, IconCode, IconSwipeDown } from '@tabler/icons-react';

const ProjectsPage = (props) => {
    const data = useSelector((state) => state.data.data);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const [activeCategory, setActiveCategory] = useState('All');

    // Sort the projects data such that highlighted projects are on top
    const sortedProjects = [...data.projects].sort((a, b) => b.highlight - a.highlight);

    // Filter projects based on active category
    const filteredProjects = activeCategory === 'All' ? sortedProjects : sortedProjects.filter(project => project.category.toLowerCase() === activeCategory.toLowerCase());

    // Function to handle category filter clicks
    const handleCategoryClick = (category) => {
        setActiveCategory(category);
    };

    // Get unique categories from projects
    const categories = ['All', ...new Set(data.projects.map(project => project.category))];

    return (
        <div className='ml-auto mr-auto overflow-hidden max-w-5xl'>
            <div className="m-4 min-h-screen sm:mt-40 flex md:flex-col lg:min-h-96">
                <div className="flex-1 flex flex-col items-center justify-center">
                    <motion.div className="text-3xl sm:text-xl text-center text-gray-300 max-w-3xl"
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Welcome to the projects section. Here is a glimpse of some of the coolest ideas I have worked on and transformed into reality. Feel free to explore and view the source codes!
                    </motion.div>
                    <div className='flex sm:flex-col justify-center items-center mt-6 sm:mt-8'>
                        <motion.div
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <Link to={`/#contact`}><div className="btn sm:w-fit sm:mr-0 sm:mb-4 mr-4"><span>{`>.  `}</span>Let's Connect.</div></Link>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            <Link to={`/about`}><div className="btn alt-btn sm:w-fit sm:mr-0 sm:mb-4 mr-4"><span>{`>.  `}</span>About Me.</div></Link>
                        </motion.div>
                    </div>
                </div>
            </div>
            <motion.div
                style={{ flex: 0.6 }} className="absolute swiper bottom-7 lg:hidden cursor-pointer"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.75 }}
            >
                <IconSwipeDown size={36} className='text-gray-600' stroke={1.5} />
            </motion.div>

            {/* Category Filter */}
            <div className="flex justify-center mt-20 sm:mt-32 mb-10">
                {categories.map((category, index) => (
                    <button
                        key={index}
                        className={`font-semibold sm:text-sm sm:mx-2 text-lg mx-4 border-b-2 ${category === activeCategory ? 'border-b-white' : ' border-b-transparent'}`}
                        onClick={() => handleCategoryClick(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-6 p-4">
                {filteredProjects.map((project, index) => (
                    <Link key={index} to={project.link} target="_blank">
                        <motion.div
                            className={`p-4 rounded-xl relative group projectCard ${project.highlight ? 'border border-yellow-500' : ''}`}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            variants={{
                                hidden: { opacity: 0, y: 100 },
                                visible: { opacity: 1, y: 0 },
                            }}
                        >
                            <div
                                style={{ background: "#363636" }}
                                className="playbutton absolute top-4 right-4 opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity size-8 flex justify-center items-center rounded-xl duration-300"
                            >
                                {project.mainLanguage === "Python" ? <IconBrandPython size={20} color='#ccc' /> : 
                                project.mainLanguage === "Django" ? <IconBrandDjango size={20} color='#ccc' /> : 
                                project.mainLanguage === "React" ? <IconBrandReact size={20} color='#ccc' /> : 
                                project.mainLanguage === "Android" ? <IconBrandAndroid size={20} color='#ccc' /> : 
                                project.mainLanguage === "Python" ? <IconBrandPython size={20} color='#ccc' /> : 
                                <IconCode size={20} color='#ccc' />}
                            </div>
                            <p className="font-semibold text-sm mb-2"><span>{`>./`}</span>{project.category.toLowerCase()}</p>
                            <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
                            <p className="text-gray-400 text-sm mb-4">{`// `}{project.desc}</p>
                            <p className="text-gray-500 text-sm"><strong>Tech Stack:</strong> {project.tech}</p>
                        </motion.div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default ProjectsPage;
